/**
 * Página de Planilha Administrativa
 * Interface em formato de planilha para criação rápida de demandas e jobs
 * 
 * @author Equipe GestaoeCRM
 * @date 2025-01-15
 */

import { useState, useEffect, useRef } from 'react';
import { useForm, useFieldArray, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { 
  Plus, 
  Trash2, 
  FileText, 
  Save,
  ArrowDown,
  Building2,
  User,
  ClipboardList,
  Upload,
  CheckCircle,
  X
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import toast from 'react-hot-toast';
import LoadingSpinner from '../components/LoadingSpinner';
import AdvancedFileManagerModal from '../components/AdvancedFileManagerModal';

// Schema de validação para cada linha da planilha
const spreadsheetRowSchema = z.object({
  id: z.string().optional(),
  data: z.string().default(() => new Date().toLocaleDateString('pt-BR')),
  escopoDemanda: z.object({
    type: z.enum(['existing', 'new']),
    value: z.string().optional(),
    newDemandTitle: z.string().optional()
  }).refine((data) => {
    if (data.type === 'existing') {
      return data.value && data.value.trim() !== '';
    }
    if (data.type === 'new') {
      return data.newDemandTitle && data.newDemandTitle.trim() !== '';
    }
    return false;
  }, {
    message: 'Escopo/Demanda é obrigatório',
    path: ['value']
  }),
  clienteArea: z.object({
    type: z.enum(['cliente', 'area']),
    value: z.string().min(1, 'Cliente/Área é obrigatório')
  }),
  jobTask: z.string().min(1, 'JOB/TASK é obrigatório'),
  descricao: z.string().min(1, 'Descrição é obrigatória'),
  gestor: z.string().min(1, 'Gestor é obrigatório'),
  responsavel: z.string().min(1, 'Responsável é obrigatório'),
  arquivos: z.array(z.string()).default([]),
  isGenerated: z.boolean().default(false),
  isPlaceholder: z.boolean().default(false)
});

const spreadsheetSchema = z.object({
  rows: z.array(spreadsheetRowSchema)
});

type SpreadsheetRow = z.infer<typeof spreadsheetRowSchema>;
type SpreadsheetData = z.infer<typeof spreadsheetSchema>;

interface Demand {
  id: string;
  title: string;
  description: string;
  status: string;
}

interface Client {
  id: string;
  name: string;
  email: string;
}

interface Area {
  id: string;
  name: string;
  description: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  areaId?: string;
}

export default function AdminSpreadsheetPage() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [demands, setDemands] = useState<Demand[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [areas, setAreas] = useState<Area[]>([]);
  const [managers, setManagers] = useState<User[]>([]);
  const [analysts, setAnalysts] = useState<User[]>([]);
  const [showFileModal, setShowFileModal] = useState(false);
  const [showNewDemandModal, setShowNewDemandModal] = useState(false);
  const [currentRowIndex, setCurrentRowIndex] = useState<number | null>(null);
  const [generatingRows, setGeneratingRows] = useState<Set<number>>(new Set());

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
    reset
  } = useForm<SpreadsheetData>({
    resolver: zodResolver(spreadsheetSchema),
    defaultValues: {
      rows: [
        {
          data: new Date().toLocaleDateString('pt-BR'),
          escopoDemanda: { type: 'existing', value: '' },
          clienteArea: { type: 'cliente', value: '' },
          jobTask: '',
          descricao: '',
          gestor: user?.role === 'GESTOR' ? user.id : '',
          responsavel: '',
          arquivos: [],
          isGenerated: false,
          isPlaceholder: false
        }
      ]
    }
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows'
  });

  const watchedRows = watch('rows');

  // Carregar dados iniciais
  useEffect(() => {
    loadInitialData();
  }, []);

  // Carregar dados salvos da planilha após carregar dados iniciais
  useEffect(() => {
    if (!loading && demands.length > 0) {
      loadSavedSpreadsheetData();
    }
  }, [loading, demands.length]);

  // Salvar dados da planilha sempre que houver mudanças (com debounce)
  useEffect(() => {
    if (watchedRows && watchedRows.length > 0) {
      const timeoutId = setTimeout(() => {
        saveSpreadsheetData();
      }, 1000); // Debounce de 1 segundo

      return () => clearTimeout(timeoutId);
    }
  }, [watchedRows]);

  const loadInitialData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadDemands(),
        loadClients(),
        loadAreas(),
        loadManagers(),
        loadAnalysts()
      ]);
    } catch (error) {
      // console.error('Erro ao carregar dados iniciais:', error);
      toast.error('Erro ao carregar dados iniciais');
    } finally {
      setLoading(false);
    }
  };

  // Carregar dados salvos da planilha da API
  const loadSavedSpreadsheetData = async () => {
    try {
      const response = await api.get('/admin-spreadsheet/load');
      
      if (response.data.success && response.data.data) {
        const savedData = response.data.data;
        
        if (savedData.data && savedData.data.rows && savedData.data.rows.length > 0) {
          // Filtrar apenas linhas que não são placeholders vazias
          const validRows = savedData.data.rows.filter((row: any) => 
            !row.isPlaceholder || 
            (row.jobTask && row.jobTask.trim() !== '') ||
            (row.descricao && row.descricao.trim() !== '') ||
            (row.escopoDemanda?.value && row.escopoDemanda.value.trim() !== '') ||
            (row.clienteArea?.value && row.clienteArea.value.trim() !== '') ||
            (row.gestor && row.gestor.trim() !== '') ||
            (row.responsavel && row.responsavel.trim() !== '') ||
            (row.arquivos && row.arquivos.length > 0)
          );
          
          // Sempre adicionar uma linha placeholder no final se não existir
          const hasPlaceholder = validRows.some((row: any) => row.isPlaceholder);
          if (!hasPlaceholder) {
            const gestorId = user?.role === 'GESTOR' ? user.id : '';
            validRows.push({
              data: new Date().toLocaleDateString('pt-BR'),
              escopoDemanda: { type: 'existing' as const, value: '' },
              clienteArea: { type: 'cliente' as const, value: '' },
              jobTask: '',
              descricao: '',
              gestor: gestorId,
              responsavel: '',
              arquivos: [],
              isGenerated: false,
              isPlaceholder: true
            });
          }
          
          if (validRows.length > 0) {
            reset({ rows: validRows });
            // console.log('📊 Dados da planilha carregados da API:', validRows.length, 'linhas');
          }
        } else {
          // Se não há dados salvos, criar uma linha inicial com placeholder
          const gestorId = user?.role === 'GESTOR' ? user.id : '';
          const initialRows = [
            {
              data: new Date().toLocaleDateString('pt-BR'),
              escopoDemanda: { type: 'existing' as const, value: '' },
              clienteArea: { type: 'cliente' as const, value: '' },
              jobTask: '',
              descricao: '',
              gestor: gestorId,
              responsavel: '',
              arquivos: [],
              isGenerated: false,
              isPlaceholder: true
            }
          ];
          reset({ rows: initialRows });
          // console.log('📊 Linha inicial criada com placeholder');
        }
      }
    } catch (error) {
      // console.error('Erro ao carregar dados salvos da planilha:', error);
      // Em caso de erro, criar uma linha inicial com placeholder
      const gestorId = user?.role === 'GESTOR' ? user.id : '';
      const initialRows = [
        {
          data: new Date().toLocaleDateString('pt-BR'),
          escopoDemanda: { type: 'existing' as const, value: '' },
          clienteArea: { type: 'cliente' as const, value: '' },
          jobTask: '',
          descricao: '',
          gestor: gestorId,
          responsavel: '',
          arquivos: [],
          isGenerated: false,
          isPlaceholder: true
        }
      ];
      reset({ rows: initialRows });
    }
  };

  // Salvar dados da planilha na API
  const saveSpreadsheetData = async () => {
    try {
      const dataToSave = {
        rows: watchedRows
      };
      
      await api.post('/admin-spreadsheet/save', dataToSave);
      // console.log('💾 Dados da planilha salvos na API');
    } catch (error) {
      // console.error('Erro ao salvar dados da planilha:', error);
      // Em caso de erro, não mostrar toast para não incomodar o usuário
    }
  };

  // Limpar dados da planilha
  const clearSpreadsheetData = async () => {
    if (window.confirm('Deseja realmente limpar todos os dados da planilha? Esta ação não pode ser desfeita.')) {
      try {
        await api.delete('/admin-spreadsheet/clear');
        
        reset({
          rows: [
            {
              data: new Date().toLocaleDateString('pt-BR'),
              escopoDemanda: { type: 'existing', value: '' },
              clienteArea: { type: 'cliente', value: '' },
              jobTask: '',
              descricao: '',
              gestor: user?.role === 'GESTOR' ? user.id : '',
              responsavel: '',
              arquivos: [],
              isGenerated: false,
              isPlaceholder: false
            }
          ]
        });
        
        toast.success('Planilha limpa com sucesso');
      } catch (error) {
        // console.error('Erro ao limpar dados da planilha:', error);
        toast.error('Erro ao limpar dados da planilha');
      }
    }
  };

  // Excluir arquivos do servidor
  const deleteFilesFromServer = async (fileIds: string[], demandId?: string) => {
    if (!fileIds || fileIds.length === 0 || !demandId) {
      return;
    }

    try {
      // Excluir cada arquivo individualmente
      const deletePromises = fileIds.map(fileId => 
        api.delete(`/demand-files/${demandId}/files/${fileId}`)
      );
      
      await Promise.all(deletePromises);
      // console.log('✅ Todos os arquivos foram excluídos com sucesso');
    } catch (error) {
      // console.error('Erro ao excluir arquivos:', error);
      throw error;
    }
  };

  const loadDemands = async () => {
    try {
      // Construir parâmetros de filtro baseado no role do usuário
      let url = '/demands';
      const params = new URLSearchParams();
      
      // Admin vê todas as demandas, gestor vê apenas suas demandas
      if (user?.role === 'GESTOR') {
        params.append('managerId', user.id);
      }
      // ANALISTA não deveria ter acesso à planilha administrativa, mas se tiver, vê apenas suas demandas
      if (user?.role === 'ANALISTA') {
        params.append('assigneeId', user.id);
      }
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }
      
      const response = await api.get(url);
      const demandsData = response.data.data || response.data || [];
      setDemands(demandsData);
    } catch (error) {
      // console.error('Erro ao carregar demandas:', error);
      toast.error('Erro ao carregar demandas');
    }
  };

  const loadClients = async () => {
    try {
      const response = await api.get('/clients');
      const clientsData = response.data.data || response.data || [];
      setClients(clientsData);
    } catch (error) {
      // console.error('Erro ao carregar clientes:', error);
      toast.error('Erro ao carregar clientes');
    }
  };

  const loadAreas = async () => {
    try {
      const response = await api.get('/company-areas');
      const areasData = response.data.data || response.data || [];
      setAreas(areasData);
    } catch (error) {
      // console.error('Erro ao carregar áreas:', error);
      toast.error('Erro ao carregar áreas');
    }
  };

  const loadManagers = async () => {
    try {
      const response = await api.get('/users');
      const usersData = response.data.data || response.data || [];
      const managersData = usersData.filter((u: User) => u.role === 'GESTOR' || u.role === 'ADMIN');
      setManagers(managersData);
    } catch (error) {
      // console.error('Erro ao carregar gestores:', error);
      toast.error('Erro ao carregar gestores');
    }
  };

  const loadAnalysts = async () => {
    try {
      const response = await api.get('/users');
      const usersData = response.data.data || response.data || [];
      const analystsData = usersData.filter((u: User) => u.role === 'ANALISTA');
      setAnalysts(analystsData);
    } catch (error) {
      // console.error('Erro ao carregar analistas:', error);
      toast.error('Erro ao carregar analistas');
    }
  };

  // Adicionar nova linha
  const addNewRow = () => {
    // Se o usuário for gestor, atribuir automaticamente
    const gestorId = user?.role === 'GESTOR' ? user.id : '';
    
    // Adicionar linha normal
    append({
      data: new Date().toLocaleDateString('pt-BR'),
      escopoDemanda: { type: 'existing', value: '' },
      clienteArea: { type: 'cliente', value: '' },
      jobTask: '',
      descricao: '',
      gestor: gestorId,
      responsavel: '',
      arquivos: [],
      isGenerated: false,
      isPlaceholder: false
    });
    
    // Sempre adicionar uma linha placeholder após adicionar uma linha normal
    addPlaceholderRow();
  };

  // Adicionar linha placeholder (usado apenas para navegação por teclado)
  const addPlaceholderRow = () => {
    const gestorId = user?.role === 'GESTOR' ? user.id : '';
    
    append({
      data: new Date().toLocaleDateString('pt-BR'),
      escopoDemanda: { type: 'existing', value: '' },
      clienteArea: { type: 'cliente', value: '' },
      jobTask: '',
      descricao: '',
      gestor: gestorId,
      responsavel: '',
      arquivos: [],
      isGenerated: false,
      isPlaceholder: true
    });
  };

  // Remover linha com confirmação
  const removeRow = async (index: number) => {
    const row = watchedRows[index];
    
    // Verificar se a linha tem dados preenchidos
    const hasData = row && (
      (row.jobTask && row.jobTask.trim() !== '') ||
      (row.descricao && row.descricao.trim() !== '') ||
      (row.escopoDemanda?.value && row.escopoDemanda.value.trim() !== '') ||
      (row.clienteArea?.value && row.clienteArea.value.trim() !== '') ||
      (row.gestor && row.gestor.trim() !== '') ||
      (row.responsavel && row.responsavel.trim() !== '') ||
      (row.arquivos && row.arquivos.length > 0)
    );

    if (hasData) {
      // Mostrar confirmação para linhas com dados
      const confirmMessage = `Esta linha contém dados preenchidos. Deseja realmente excluí-la?\n\nDados que serão perdidos:\n${row.jobTask ? `• JOB/TASK: ${row.jobTask}\n` : ''}${row.descricao ? `• Descrição: ${row.descricao.substring(0, 50)}...\n` : ''}${row.arquivos && row.arquivos.length > 0 ? `• ${row.arquivos.length} arquivo(s) anexado(s)\n` : ''}\n\nEsta ação não pode ser desfeita.`;
      
      if (!window.confirm(confirmMessage)) {
        return; // Usuário cancelou
      }
    }

    // Se a linha tem arquivos, remover arquivos também
    if (row && row.arquivos && row.arquivos.length > 0) {
      try {
        // Excluir arquivos do servidor
        await deleteFilesFromServer(row.arquivos, row.escopoDemanda?.value);
        // console.log('🗑️ Arquivos removidos com sucesso:', row.arquivos);
      } catch (error) {
        // console.error('Erro ao remover arquivos:', error);
        toast.error('Erro ao remover arquivos. Verifique se os arquivos ainda existem.');
      }
    }

    remove(index);
    
    // Verificar se ainda há uma linha placeholder após remover
    const remainingRows = watchedRows.filter((_, i) => i !== index);
    const hasPlaceholder = remainingRows.some(row => row.isPlaceholder);
    
    // Se não há placeholder, adicionar um
    if (!hasPlaceholder) {
      addPlaceholderRow();
    }
    
    toast.success(hasData ? 'Linha excluída com sucesso' : 'Linha removida');
  };

  // Gerar JOB/TASK
  const generateJobTask = async (rowIndex: number) => {
    try {
      setGeneratingRows(prev => new Set(prev).add(rowIndex));
      
      const row = watchedRows[rowIndex];
      
      // Validar linha antes de gerar
      // console.log('🔍 DEBUG: Dados da linha antes da validação:', {
      // row,
      // clienteArea: row.clienteArea,
      // clienteAreaValue: row.clienteArea?.value,
      // clienteAreaType: row.clienteArea?.type
      // });
      
      const validation = spreadsheetRowSchema.safeParse(row);
      if (!validation.success) {
        // console.error('❌ Erro de validação:', validation.error.errors);
        toast.error('Por favor, preencha todos os campos obrigatórios');
        return;
      }

      // Criar demanda se for nova
      let demandId = row.escopoDemanda.value;
      if (row.escopoDemanda.type === 'new') {
        // console.log('🆕 Criando nova demanda:', {
        // title: row.escopoDemanda.newDemandTitle,
        // description: row.descricao,
        // managerId: row.gestor,
        // assigneeId: row.responsavel
        // });
        
        const demandResponse = await api.post('/demands', {
          title: row.escopoDemanda.newDemandTitle,
          description: row.descricao,
          assigneeId: row.responsavel, // Sempre enviar o responsável selecionado
          priority: 'MEDIA',
          status: 'PENDENTE',
          clientAreaType: row.clienteArea?.type,
          clientAreaValue: row.clienteArea?.value
        });
        
        // console.log('📋 Resposta da criação de demanda:', demandResponse.data);
        
        if (demandResponse.data.success) {
          demandId = demandResponse.data.data.id;
          // console.log('✅ Demanda criada com ID:', demandId);
        } else {
          // console.error('❌ Erro ao criar demanda:', demandResponse.data.message);
          throw new Error(demandResponse.data.message || 'Erro ao criar demanda');
        }
      }

      // Criar OS (Order of Service)
      // console.log('🔧 Criando OS:', {
      // title: row.jobTask,
      // description: row.descricao,
      // type: 'OUTRO',
      // demandId: demandId,
      // priority: 'MEDIA',
      // responsavel: row.responsavel
      // });
      
      const osResponse = await api.post('/os', {
        title: row.jobTask,
        description: row.descricao,
        type: 'OUTRO', // Sempre usar OUTRO para OS criadas pela planilha
        demandId: demandId,
        priority: 'MEDIA',
        responsavel: row.responsavel // Enviar o responsável selecionado
        // status é definido automaticamente pelo backend
      });

      // console.log('📋 Resposta da criação de OS:', osResponse.data);

      if (osResponse.data.success) {
        // Marcar linha como gerada
        setValue(`rows.${rowIndex}.isGenerated`, true);
        
        // Não remover linha automaticamente - deixar usuário decidir
        toast.success('JOB/TASK gerado com sucesso! A linha foi marcada como concluída.');
        // console.log('✅ OS criada com sucesso');
      } else {
        // console.error('❌ Erro ao criar OS:', osResponse.data.message);
        throw new Error(osResponse.data.message || 'Erro ao gerar JOB/TASK');
      }
    } catch (error) {
      // console.error('Erro ao gerar JOB/TASK:', error);
      
      // Mostrar mensagem de erro mais específica
      if (error instanceof Error) {
        toast.error(`Erro ao gerar JOB/TASK: ${error.message}`);
      } else {
        toast.error('Erro ao gerar JOB/TASK. Verifique os dados e tente novamente.');
      }
    } finally {
      setGeneratingRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(rowIndex);
        return newSet;
      });
    }
  };

  // Abrir modal de arquivos
  const openFileModal = (rowIndex: number) => {
    setCurrentRowIndex(rowIndex);
    setShowFileModal(true);
  };

  // Fechar modal de arquivos
  const closeFileModal = () => {
    setShowFileModal(false);
    setCurrentRowIndex(null);
  };

  // Abrir modal de nova demanda
  const openNewDemandModal = (rowIndex: number) => {
    setCurrentRowIndex(rowIndex);
    setShowNewDemandModal(true);
  };

  // Fechar modal de nova demanda
  const closeNewDemandModal = () => {
    setShowNewDemandModal(false);
    setCurrentRowIndex(null);
  };

  // Salvar arquivos
  const saveFiles = (files: string[]) => {
    if (currentRowIndex !== null) {
      setValue(`rows.${currentRowIndex}.arquivos`, files);
    }
    closeFileModal();
  };

  // Navegação por teclado - seta para baixo adiciona nova linha
  const handleKeyDown = (event: React.KeyboardEvent, rowIndex: number) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      
      // Se a linha atual é placeholder, converter para linha normal
      if (watchedRows[rowIndex]?.isPlaceholder) {
        setValue(`rows.${rowIndex}.isPlaceholder`, false);
      }
      
      // Adicionar nova linha placeholder
      addPlaceholderRow();
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-text-primary">
            Planilha Administrativa
          </h1>
          <p className="text-text-muted">
            Criação rápida de demandas e jobs/tasks - Clique em "+ Nova linha" ou use seta ↓ para adicionar nova linha
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={clearSpreadsheetData}
            className="px-4 py-2 bg-red-600 text-white rounded-glass hover:bg-red-700 flex items-center gap-2"
            title="Limpar todos os dados da planilha"
          >
            <Trash2 className="h-4 w-4" />
            Limpar Planilha
          </button>
        </div>
      </div>

      {/* Planilha */}
      <div className="bg-background-glass backdrop-blur-glass rounded-glass border border-border-glass overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            {/* Cabeçalho */}
            <thead className="bg-background-glass-hover">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  DATA
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  ESCOPO/DEMANDA
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  CLIENTE/ÁREA DA EMPRESA
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  JOB/TASKS
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  DESCRIÇÃO
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  GESTOR
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  RESPONSÁVEL
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary border-r border-border-glass">
                  ARQUIVOS
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-text-primary">
                  AÇÕES
                </th>
              </tr>
            </thead>

            {/* Corpo da tabela */}
            <tbody>
              {fields.map((field, index) => (
                <SpreadsheetRow
                  key={field.id}
                  index={index}
                  control={control}
                  errors={errors}
                  demands={demands}
                  clients={clients}
                  areas={areas}
                  managers={managers}
                  analysts={analysts}
                  watchedRows={watchedRows}
                  onRemove={() => removeRow(index)}
                  onGenerate={() => generateJobTask(index)}
                  onOpenFiles={() => openFileModal(index)}
                  onOpenNewDemand={() => openNewDemandModal(index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  isGenerating={generatingRows.has(index)}
                  isGenerated={watchedRows[index]?.isGenerated || false}
                  isPlaceholder={watchedRows[index]?.isPlaceholder || false}
                  addPlaceholderRow={addPlaceholderRow}
                  setValue={setValue}
                />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal de Arquivos */}
      <AdvancedFileManagerModal
        isOpen={showFileModal}
        onClose={closeFileModal}
        onSave={saveFiles}
        demandId={currentRowIndex !== null ? watchedRows[currentRowIndex]?.escopoDemanda?.value : undefined}
        title="Gerenciar Arquivos"
        entityType="demand"
        entityInfo={currentRowIndex !== null ? {
          name: watchedRows[currentRowIndex]?.escopoDemanda?.type === 'existing' 
            ? demands.find(d => d.id === watchedRows[currentRowIndex]?.escopoDemanda?.value)?.title || 'Demanda'
            : watchedRows[currentRowIndex]?.escopoDemanda?.newDemandTitle || 'Nova Demanda',
          status: 'Em Andamento',
          priority: 'Média',
          description: watchedRows[currentRowIndex]?.descricao || ''
        } : undefined}
      />

      {/* Modal de Nova Demanda */}
      {showNewDemandModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-background-glass backdrop-blur-glass rounded-glass border border-border-glass p-6 max-w-md w-full mx-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-text-primary">Nova Demanda</h3>
              <button
                onClick={closeNewDemandModal}
                className="text-text-muted hover:text-text-primary"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-text-primary mb-1">
                  Título da Demanda
                </label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-border-glass rounded-glass bg-background-glass text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Digite o título da demanda"
                  onChange={(e) => {
                    if (currentRowIndex !== null) {
                      setValue(`rows.${currentRowIndex}.escopoDemanda.newDemandTitle`, e.target.value);
                    }
                  }}
                />
              </div>
              <div className="flex justify-end space-x-2">
                <button
                  onClick={closeNewDemandModal}
                  className="px-4 py-2 text-text-muted hover:text-text-primary"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (currentRowIndex !== null) {
                      setValue(`rows.${currentRowIndex}.escopoDemanda.type`, 'new');
                    }
                    closeNewDemandModal();
                  }}
                  className="px-4 py-2 bg-primary-600 text-white rounded-glass hover:bg-primary-700"
                >
                  Salvar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Componente para cada linha da planilha
interface SpreadsheetRowProps {
  index: number;
  control: any;
  errors: any;
  demands: Demand[];
  clients: Client[];
  areas: Area[];
  managers: User[];
  analysts: User[];
  watchedRows: SpreadsheetRow[];
  onRemove: () => void;
  onGenerate: () => void;
  onOpenFiles: () => void;
  onOpenNewDemand: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  isGenerating: boolean;
  isGenerated: boolean;
  isPlaceholder: boolean;
  addPlaceholderRow: () => void;
  setValue: any;
}

function SpreadsheetRow({
  index,
  control,
  errors,
  demands,
  clients,
  areas,
  managers,
  analysts,
  watchedRows,
  onRemove,
  onGenerate,
  onOpenFiles,
  onOpenNewDemand,
  onKeyDown,
  isGenerating,
  isGenerated,
  isPlaceholder,
  addPlaceholderRow,
  setValue
}: SpreadsheetRowProps) {
  const escopoDemanda = useWatch({ control, name: `rows.${index}.escopoDemanda` });
  const clienteArea = useWatch({ control, name: `rows.${index}.clienteArea` });
  const gestor = useWatch({ control, name: `rows.${index}.gestor` });

  // Debug para verificar os valores
  // console.log('🔍 DEBUG SpreadsheetRow:', {
  // index,
  // escopoDemanda,
  // clienteArea,
  // gestor,
  // isPlaceholder
  // });

  // Filtrar analistas baseado na área do gestor selecionado
  const filteredAnalysts = gestor 
    ? analysts.filter(analyst => analyst.areaId === managers.find(m => m.id === gestor)?.areaId)
    : analysts;

  // Se for linha placeholder, mostrar apenas campo simples
  if (isPlaceholder) {
    return (
      <tr className="hover:bg-background-glass-hover bg-background-glass-hover">
        <td colSpan={9} className="px-4 py-3">
          <input
            type="text"
            placeholder="+ Adicionar nova linha"
            className="w-full px-4 py-3 border-2 border-dashed border-primary-300 rounded-glass bg-transparent text-text-muted focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all duration-200 text-center"
            onFocus={() => {
              // Converter placeholder em linha normal
              setValue(`rows.${index}.isPlaceholder`, false);
              addPlaceholderRow();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === 'ArrowDown') {
                e.preventDefault();
                setValue(`rows.${index}.isPlaceholder`, false);
                addPlaceholderRow();
              }
            }}
          />
        </td>
      </tr>
    );
  }

  return (
    <tr className="hover:bg-background-glass-hover">
      {/* DATA */}
      <td className="px-4 py-3 border-r border-border-glass">
        <input
          type="text"
          {...control.register(`rows.${index}.data`)}
          className="w-full px-3 py-2 border border-border-glass rounded-glass bg-background-glass text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          readOnly
        />
      </td>

      {/* ESCOPO/DEMANDA */}
      <td className="px-4 py-3 border-r border-border-glass">
        <div className="space-y-2">
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="existing"
                checked={escopoDemanda?.type === 'existing'}
                onChange={(e) => {
                  setValue(`rows.${index}.escopoDemanda.type`, e.target.value);
                  setValue(`rows.${index}.escopoDemanda.value`, '');
                  setValue(`rows.${index}.escopoDemanda.newDemandTitle`, '');
                }}
                className="mr-2"
              />
              <span className="text-sm text-text-primary">Existente</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="new"
                checked={escopoDemanda?.type === 'new'}
                onChange={(e) => {
                  setValue(`rows.${index}.escopoDemanda.type`, e.target.value);
                  setValue(`rows.${index}.escopoDemanda.value`, '');
                  setValue(`rows.${index}.escopoDemanda.newDemandTitle`, '');
                }}
                className="mr-2"
              />
              <span className="text-sm text-text-primary">Nova</span>
            </label>
          </div>
          
          {escopoDemanda?.type === 'existing' ? (
            <div className="flex gap-2">
              <select
                {...control.register(`rows.${index}.escopoDemanda.value`)}
                className="input flex-1"
                onKeyDown={onKeyDown}
              >
                <option value="">Selecione uma demanda</option>
                {demands.map(demand => (
                  <option key={demand.id} value={demand.id}>
                    {demand.title}
                  </option>
                ))}
              </select>
            </div>
          ) : (
            <div className="flex gap-2">
              <input
                type="text"
                {...control.register(`rows.${index}.escopoDemanda.newDemandTitle`)}
                placeholder="Título da nova demanda"
                className="flex-1 px-3 py-2 border border-border-glass rounded-glass bg-background-glass text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
                onKeyDown={onKeyDown}
              />
              <button
                type="button"
                onClick={onOpenNewDemand}
                className="px-3 py-2 bg-primary-600 text-white rounded-glass hover:bg-primary-700 text-sm"
              >
                <Plus className="h-4 w-4" />
              </button>
            </div>
          )}
          
          {errors.rows?.[index]?.escopoDemanda && (
            <p className="text-red-500 text-xs">
              {errors.rows[index].escopoDemanda.message}
            </p>
          )}
        </div>
      </td>

      {/* CLIENTE/ÁREA */}
      <td className="px-4 py-3 border-r border-border-glass">
        <div className="space-y-2">
          <div className="flex gap-4">
            <label className="flex items-center">
              <input
                type="radio"
                value="cliente"
                checked={clienteArea?.type === 'cliente'}
                onChange={(e) => {
                  setValue(`rows.${index}.clienteArea.type`, e.target.value);
                  setValue(`rows.${index}.clienteArea.value`, '');
                }}
                className="mr-2"
              />
              <span className="text-sm text-text-primary">Cliente</span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                value="area"
                checked={clienteArea?.type === 'area'}
                onChange={(e) => {
                  setValue(`rows.${index}.clienteArea.type`, e.target.value);
                  setValue(`rows.${index}.clienteArea.value`, '');
                }}
                className="mr-2"
              />
              <span className="text-sm text-text-primary">Área</span>
            </label>
          </div>
          
          {clienteArea?.type === 'cliente' ? (
            <select
              name={`rows.${index}.clienteArea.value`}
              value={clienteArea?.value || ''}
              onChange={(e) => setValue(`rows.${index}.clienteArea.value`, e.target.value)}
              className="input"
              onKeyDown={onKeyDown}
            >
              <option value="">Selecione um cliente</option>
              {clients.map(client => (
                <option key={client.id} value={client.id}>
                  {client.name}
                </option>
              ))}
            </select>
          ) : (
            <select
              name={`rows.${index}.clienteArea.value`}
              value={clienteArea?.value || ''}
              onChange={(e) => setValue(`rows.${index}.clienteArea.value`, e.target.value)}
              className="input"
              onKeyDown={onKeyDown}
            >
              <option value="">Selecione uma área</option>
              {areas.map(area => (
                <option key={area.id} value={area.id}>
                  {area.name}
                </option>
              ))}
            </select>
          )}
          
          {errors.rows?.[index]?.clienteArea && (
            <p className="text-red-500 text-xs">
              {errors.rows[index].clienteArea.message}
            </p>
          )}
        </div>
      </td>

      {/* JOB/TASKS */}
      <td className="px-4 py-3 border-r border-border-glass">
        <input
          type="text"
          {...control.register(`rows.${index}.jobTask`)}
          placeholder="Título do JOB/TASK"
          className="w-full px-3 py-2 border border-border-glass rounded-glass bg-background-glass text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          onKeyDown={onKeyDown}
        />
        {errors.rows?.[index]?.jobTask && (
          <p className="text-red-500 text-xs">
            {errors.rows[index].jobTask.message}
          </p>
        )}
      </td>

      {/* DESCRIÇÃO */}
      <td className="px-4 py-3 border-r border-border-glass">
        <textarea
          {...control.register(`rows.${index}.descricao`)}
          placeholder="Descrição detalhada"
          rows={2}
          className="w-full px-3 py-2 border border-border-glass rounded-glass bg-background-glass text-text-primary focus:outline-none focus:ring-2 focus:ring-primary-500"
          onKeyDown={onKeyDown}
        />
        {errors.rows?.[index]?.descricao && (
          <p className="text-red-500 text-xs">
            {errors.rows[index].descricao.message}
          </p>
        )}
      </td>

      {/* GESTOR */}
      <td className="px-4 py-3 border-r border-border-glass">
        <select
          {...control.register(`rows.${index}.gestor`)}
          className="input"
          onKeyDown={onKeyDown}
        >
          <option value="">Selecione um gestor</option>
          {managers.map(manager => (
            <option key={manager.id} value={manager.id}>
              {manager.name}
            </option>
          ))}
        </select>
        {errors.rows?.[index]?.gestor && (
          <p className="text-red-500 text-xs">
            {errors.rows[index].gestor.message}
          </p>
        )}
      </td>

      {/* RESPONSÁVEL */}
      <td className="px-4 py-3 border-r border-border-glass">
        <select
          {...control.register(`rows.${index}.responsavel`)}
          className="input"
          onKeyDown={onKeyDown}
        >
          <option value="">Selecione um responsável</option>
          {/* Opção do próprio gestor */}
          {gestor && (
            <option value={gestor}>
              {managers.find(m => m.id === gestor)?.name} (Gestor)
            </option>
          )}
          {/* Analistas da área do gestor */}
          {filteredAnalysts.map(analyst => (
            <option key={analyst.id} value={analyst.id}>
              {analyst.name} (Analista)
            </option>
          ))}
        </select>
        {errors.rows?.[index]?.responsavel && (
          <p className="text-red-500 text-xs">
            {errors.rows[index].responsavel.message}
          </p>
        )}
      </td>

      {/* ARQUIVOS */}
      <td className="px-4 py-3 border-r border-border-glass">
        <button
          type="button"
          onClick={onOpenFiles}
          className="text-blue-400 hover:text-blue-300 flex items-center gap-1 transition-colors"
          title={`${watchedRows?.[index]?.arquivos?.length || 0} arquivo(s)`}
        >
          <Upload className="h-4 w-4" />
        </button>
      </td>

      {/* AÇÕES */}
      <td className="px-4 py-3">
        <div className="flex items-center gap-2">
          {isGenerated ? (
            <div className="flex items-center text-green-600">
              <CheckCircle className="h-4 w-4 mr-1" />
              <span className="text-sm">Gerado</span>
            </div>
          ) : (
            <button
              type="button"
              onClick={onGenerate}
              disabled={isGenerating}
              className="bg-green-600 text-white px-3 py-1 rounded text-sm hover:bg-green-700 disabled:opacity-50 flex items-center gap-1"
            >
              {isGenerating ? (
                <LoadingSpinner size="sm" />
              ) : (
                <>
                  <Save className="h-3 w-3" />
                  Gerar JOB/TASK
                </>
              )}
            </button>
          )}
          
          {!isPlaceholder && (
            <button
              type="button"
              onClick={onRemove}
              className="text-red-600 hover:text-red-800"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          )}
        </div>
      </td>
    </tr>
  );
}